import type { StageTabRole, StudioView } from "./stagingModel";
import { unsavedScripts } from "./unsavedScripts";
import { listTabNames, schemaDraftTab, type UnsavedTab } from "./unsavedTabs";

// What keeps a pipeline from deploying (TTTL-254). A draft may hold
// unsaved and unattached work; a deployed pipeline may not — it is
// complete and saved, or it does not deploy. The Deploy press lists what
// is left in a blocking modal, by kind and never by file. Editor errors
// are not here: they already disable Deploy through the Unresolved list.
//
// - A missing source, or a missing sink (unless the code declares
//   `mode = "inventory"`, which the engine runs without one). Stages view
//   only — the Full view deploys its code, which its lint checks.
// - An unsaved script: a stored script with edits, or an untitled one.
//   The deploy would run the saved file, or leave the untitled one out.
// - An unsaved schema: a schema editor holding a draft.
// - A schema waiting in a tab's bottom block and attached to no stage —
//   the rows the tab already warns will not be applied on run.
//
// The schema lines name their tabs: a schema can sit in any of three, and
// nothing outside the tab shows it. Scripts only ever live on Transform.

export interface DeployBlockerInput {
  studioView: StudioView;
  sources: ReadonlyArray<{ id: string }>;
  sinks: ReadonlyArray<{ id: string }>;
  transforms: ReadonlyArray<{ id: string; name: string }>;
  /** The pipeline code's passthrough top-level fields (`mode` among them). */
  extraFields: Record<string, unknown>;
  scriptDrafts: Record<string, string>;
  schemaDrafts: Record<string, string>;
  schemas: ReadonlyArray<{
    pending?: readonly StageTabRole[];
    attachments: readonly unknown[];
  }>;
}

const oneOrMany = (count: number, one: string, many: string) =>
  count === 0 ? [] : [count === 1 ? one : many];

const TAB_ORDER: UnsavedTab[] = ["Source", "Transform", "Sink"];
const TAB_OF_ROLE: Record<StageTabRole, UnsavedTab> = {
  source: "Source",
  transform: "Transform",
  sink: "Sink",
};

/** ", in the Sink tab" / ", in the Source and Sink tabs"; nothing when no
 *  tab is known. */
function inTabs(tabs: Iterable<UnsavedTab>): string {
  const found = new Set(tabs);
  const ordered = TAB_ORDER.filter((t) => found.has(t));
  if (ordered.length === 0) return "";
  return `, in the ${listTabNames(ordered)} ${ordered.length > 1 ? "tabs" : "tab"}`;
}

// A missing source, or a missing sink the code doesn't excuse by declaring
// an inventory. Stages view only — see the header.
function missingStages(input: DeployBlockerInput): string[] {
  if (input.studioView !== "stages") return [];
  const inventory = input.extraFields.mode === "inventory";
  return [
    ...(input.sources.length === 0 ? ["No source"] : []),
    ...(input.sinks.length === 0 && !inventory ? ["No sink"] : []),
  ];
}

function unsavedScriptLines(input: DeployBlockerInput): string[] {
  const scripts = unsavedScripts(input.scriptDrafts, input.transforms);
  return oneOrMany(
    scripts.edited.length + scripts.untitled,
    "An unsaved script",
    "Unsaved scripts",
  );
}

/** The tab role of a staged stage, by row id. */
function stageRoleIn(input: DeployBlockerInput) {
  const lists: Array<[StageTabRole, ReadonlyArray<{ id: string }>]> = [
    ["source", input.sources],
    ["transform", input.transforms],
    ["sink", input.sinks],
  ];
  return (id: string): StageTabRole | null =>
    lists.find(([, list]) => list.some((s) => s.id === id))?.[0] ?? null;
}

function unsavedSchemaLines(input: DeployBlockerInput): string[] {
  const stageRole = stageRoleIn(input);
  const drafts = Object.entries(input.schemaDrafts).filter(
    ([, text]) => text.trim() !== "",
  );
  const tabs = inTabs(
    drafts
      .map(([key]) => schemaDraftTab(key, stageRole))
      .filter((tab): tab is UnsavedTab => tab !== null),
  );
  return oneOrMany(
    drafts.length,
    `An unsaved schema${tabs}`,
    `Unsaved schemas${tabs}`,
  );
}

function unattachedSchemaLines(input: DeployBlockerInput): string[] {
  const waiting = input.schemas.filter(
    (s) => (s.pending?.length ?? 0) > 0 && s.attachments.length === 0,
  );
  const tabs = inTabs(
    waiting.flatMap((s) => (s.pending ?? []).map((tab) => TAB_OF_ROLE[tab])),
  );
  return oneOrMany(
    waiting.length,
    `A schema not attached to any stage${tabs}`,
    `Schemas not attached to any stage${tabs}`,
  );
}

export function deployBlockers(input: DeployBlockerInput): string[] {
  return [
    ...missingStages(input),
    ...unsavedScriptLines(input),
    ...unsavedSchemaLines(input),
    ...unattachedSchemaLines(input),
  ];
}

/** The sentence above the modal's list. */
export function deployBlockersIntro(): string {
  return "Only a complete, saved pipeline can be deployed. This one has:";
}
